import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { supabase } from '@/integrations/supabase/client';
import { Supplier, ApprovalStatus } from '@/integrations/supabase/types';
import { ROUTE_PATHS } from '@/lib/index';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Upload, X } from 'lucide-react';

export default function SupplierForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [supplier, setSupplier] = useState<Partial<Supplier>>({
    company_name: '',
    description: '',
    email: '',
    phone: '',
    country: '',
    website_url: '',
    logo_url: '',
    status: 'pending',
    is_featured: false,
  });

  useEffect(() => {
    if (id) {
      fetchSupplier();
    }
  }, [id]);

  const fetchSupplier = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('suppliers')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      if (data) {
        setSupplier(data);
      }
    } catch (error) {
      console.error('Error fetching supplier:', error);
      toast({
        title: 'Error',
        description: 'Failed to load supplier data',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Invalid file',
        description: 'Please upload an image file',
        variant: 'destructive',
      });
      return;
    }

    try {
      setUploading(true);
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
      const filePath = `${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('supplier-logos')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('supplier-logos')
        .getPublicUrl(filePath);

      setSupplier(prev => ({ ...prev, logo_url: publicUrl }));
      toast({
        title: 'Success',
        description: 'Logo uploaded successfully',
      });
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast({
        title: 'Error',
        description: 'Failed to upload logo',
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveLogo = () => {
    setSupplier(prev => ({ ...prev, logo_url: '' }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!supplier.company_name?.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Company name is required',
        variant: 'destructive',
      });
      return;
    }

    if (supplier.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(supplier.email)) {
      toast({
        title: 'Validation Error',
        description: 'Please enter a valid email address',
        variant: 'destructive',
      });
      return;
    }

    if (supplier.website_url && !/^https?:\/\/.+/.test(supplier.website_url)) {
      toast({
        title: 'Validation Error',
        description: 'Website URL must start with http:// or https://',
        variant: 'destructive',
      });
      return;
    }

    try {
      setLoading(true);
      const slug = supplier.company_name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');

      const supplierData = {
        company_name: supplier.company_name,
        slug,
        description: supplier.description || null,
        email: supplier.email || null,
        phone: supplier.phone || null,
        country: supplier.country || null,
        website_url: supplier.website_url || null,
        logo_url: supplier.logo_url || null,
        status: supplier.status as ApprovalStatus,
        is_featured: supplier.is_featured || false,
        updated_at: new Date().toISOString(),
      };

      if (id) {
        const { error } = await supabase
          .from('suppliers')
          .update(supplierData)
          .eq('id', id);

        if (error) throw error;

        toast({
          title: 'Success',
          description: 'Supplier updated successfully',
        });
      } else {
        const { error } = await supabase
          .from('suppliers')
          .insert([{ ...supplierData, created_at: new Date().toISOString() }]);

        if (error) throw error;

        toast({
          title: 'Success',
          description: 'Supplier created successfully',
        });
      }

      navigate(ROUTE_PATHS.ADMIN_SUPPLIERS);
    } catch (error) {
      console.error('Error saving supplier:', error);
      toast({
        title: 'Error',
        description: 'Failed to save supplier',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {id ? 'Edit Supplier' : 'Add New Supplier'}
          </h1>
          <p className="text-muted-foreground mt-2">
            {id ? 'Update supplier information' : 'Create a new supplier profile'}
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Basic Information</CardTitle>
                <CardDescription>
                  Enter the supplier's company details
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="company_name">Company Name *</Label>
                  <Input
                    id="company_name"
                    value={supplier.company_name}
                    onChange={(e) => setSupplier(prev => ({ ...prev, company_name: e.target.value }))}
                    placeholder="Enter company name"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={supplier.description || ''}
                    onChange={(e) => setSupplier(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Enter company description"
                    rows={4}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={supplier.email || ''}
                      onChange={(e) => setSupplier(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="contact@company.com"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      value={supplier.phone || ''}
                      onChange={(e) => setSupplier(prev => ({ ...prev, phone: e.target.value }))}
                      placeholder="+1 234 567 8900"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="country">Country</Label>
                    <Input
                      id="country"
                      value={supplier.country || ''}
                      onChange={(e) => setSupplier(prev => ({ ...prev, country: e.target.value }))}
                      placeholder="United States"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="website_url">Website</Label>
                    <Input
                      id="website_url"
                      type="url"
                      value={supplier.website_url || ''}
                      onChange={(e) => setSupplier(prev => ({ ...prev, website_url: e.target.value }))}
                      placeholder="https://www.company.com"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Logo</CardTitle>
                <CardDescription>
                  Upload company logo (recommended size: 400x400px)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {supplier.logo_url ? (
                    <div className="relative inline-block">
                      <img
                        src={supplier.logo_url}
                        alt="Logo preview"
                        className="w-32 h-32 object-contain border border-border rounded-lg"
                      />
                      <Button
                        type="button"
                        variant="destructive"
                        size="icon"
                        className="absolute -top-2 -right-2 h-6 w-6 rounded-full"
                        onClick={handleRemoveLogo}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center w-32 h-32 border-2 border-dashed border-border rounded-lg">
                      <Upload className="h-8 w-8 text-muted-foreground" />
                    </div>
                  )}

                  <div>
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      disabled={uploading}
                      className="max-w-xs"
                    />
                    {uploading && (
                      <p className="text-sm text-muted-foreground mt-2">
                        Uploading...
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Settings</CardTitle>
                <CardDescription>
                  Configure supplier status and visibility
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="status">Status</Label>
                  <Select
                    value={supplier.status}
                    onValueChange={(value) => setSupplier(prev => ({ ...prev, status: value as ApprovalStatus }))}
                  >
                    <SelectTrigger id="status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                      <SelectItem value="on_hold">On Hold</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="is_featured">Featured Supplier</Label>
                    <p className="text-sm text-muted-foreground">
                      Display this supplier in featured sections
                    </p>
                  </div>
                  <Switch
                    id="is_featured"
                    checked={supplier.is_featured}
                    onCheckedChange={(checked) => setSupplier(prev => ({ ...prev, is_featured: checked }))}
                  />
                </div>
              </CardContent>
            </Card>

            <div className="flex gap-4">
              <Button
                type="submit"
                disabled={loading || uploading}
                className="min-w-32"
              >
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {id ? 'Update Supplier' : 'Create Supplier'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(ROUTE_PATHS.ADMIN_SUPPLIERS)}
                disabled={loading}
              >
                Cancel
              </Button>
            </div>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
}